import { toBoolean } from '../utils/utils';

/** Data entry tool (FMS) an organisation may use, block 3 of the organisation form (C2027-198). */
export type Fms = 'SMAG' | 'WIUZ' | 'GEOFOLIA' | 'WANAKA';

export const FMS_VALUES: Fms[] = ['SMAG', 'WIUZ', 'GEOFOLIA', 'WANAKA'];

// Mirrors io.wanaka.platform.domain.model.AdviceReturnType, grouped by crop for the table (PO Q33).
export const ADVICE_RETURN_TYPES_BLE = [
  'BLE_T1_REPORT',
  'BLE_T2_REPORT',
  'BLE_T2_VERSE_REPORT',
  'BLE_DN_REPORT',
  'BLE_T3_REPORT',
] as const;
export const ADVICE_RETURN_TYPES_ORGE = [
  'ORGE_T1_REPORT',
  'ORGE_T2_REPORT',
  'ORGE_T2_VERSE_REPORT',
  'ORGE_T3_REPORT',
] as const;
export const ADVICE_RETURN_TYPES_COLZA = ['COLZA_EH_REPORT', 'COLZA_SH_REPORT'] as const;

export type AdviceReturnType =
  | (typeof ADVICE_RETURN_TYPES_BLE)[number]
  | (typeof ADVICE_RETURN_TYPES_ORGE)[number]
  | (typeof ADVICE_RETURN_TYPES_COLZA)[number];

/** Only WANAKA (direct entry on the platform) has no import settings. */
export function fmsHasImportSettings(fms: Fms): boolean {
  return fms !== 'WANAKA';
}

/** Only WIUZ has "retrieve advisor from WIUZ" and the verse options. */
export function fmsHasWiuzOptions(fms: Fms): boolean {
  return fms === 'WIUZ';
}

/** Only SMAG and WIUZ can return advice to the FMS. */
export function fmsCanReturnAdvice(fms: Fms): boolean {
  return fms === 'SMAG' || fms === 'WIUZ';
}

export interface AdvisorRef {
  id: number;
  firstName?: string;
  lastName?: string;
  login?: string;
}

/** One FMS's settings within block 3, as returned by GET and sent to PUT (C2027-198). */
export interface OrganisationFmsSettings {
  fms: Fms;
  enabled: boolean;
  updateAllowed: boolean;
  defaultAdvisor: AdvisorRef | null;
  verseAllowedBle: boolean;
  verseAllowedOrge: boolean;
  retrieveAdvisorFromFms: boolean;
  returnAdvice: boolean;
  disabledReturnTypes: AdviceReturnType[];
}

/** Body of GET/PUT /organisations/{id}/fms (C2027-198). */
export interface OrganisationFmsForm {
  fms: OrganisationFmsSettings[];
  hasBeApi: boolean;
}

/** Body of PUT /organisations/{id}/fms. {@code defaultAdvisor} is sent as {id} only, or null. */
export function toFmsPayload(settings: OrganisationFmsSettings[], hasBeApi: boolean): OrganisationFmsForm {
  return {
    fms: settings.map((s) => ({
      fms: s.fms,
      enabled: toBoolean(s.enabled),
      updateAllowed: toBoolean(s.updateAllowed),
      defaultAdvisor: s.defaultAdvisor && s.defaultAdvisor.id ? { id: s.defaultAdvisor.id } : null,
      verseAllowedBle: toBoolean(s.verseAllowedBle),
      verseAllowedOrge: toBoolean(s.verseAllowedOrge),
      retrieveAdvisorFromFms: toBoolean(s.retrieveAdvisorFromFms),
      returnAdvice: toBoolean(s.returnAdvice),
      disabledReturnTypes: s.disabledReturnTypes || [],
    })) as OrganisationFmsSettings[],
    hasBeApi: toBoolean(hasBeApi),
  };
}
